import React from 'react'
import { DcButton, DcInput, PixelFactoryContext, PixelTable } from 'pixel-base'
import {
  createPeriod,
  getPeriods,
  hardClosePeriod,
  reopenPeriod,
  softClosePeriod,
} from 'utils/api-routes-accounts'
import FinanceListActions from '../../components/domain/FinanceListActions'
import FinancePageShell from '../../components/FinancePageShell'
import FinanceStatusBadge from '../../components/FinanceStatusBadge'
import FinanceEntityDrawer from '../../components/foundation/FinanceEntityDrawer'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { AccountingPeriod } from '../../types/finance'

const defaultForm = {
  name: '',
  date_start: '',
  date_end: '',
}

const Periods = () => {
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const periods = useFinanceObservable<AccountingPeriod[]>(() => getPeriods())
  const [isOpen, setIsOpen] = React.useState(false)
  const [form, setForm] = React.useState(defaultForm)

  const handleAction = (request, successMessage: string) => {
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(successMessage)
        periods.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Action failed'),
    })
  }

  const handleCreate = () => {
    createPeriod(form).subscribe({
      next: () => {
        pixelFactory?.showSuccess?.('Period created successfully')
        setForm(defaultForm)
        setIsOpen(false)
        periods.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Failed to create period'),
    })
  }

  return (
    <>
      <FinancePageShell
        title="Accounting Periods"
        isLoading={periods.loading}
        primaryAction={{ label: 'Add Period', onClick: () => setIsOpen(true) }}
      >
        <PixelTable
          columns={[
            { name: 'Name', selector: (row) => row.name },
            { name: 'Start', selector: (row) => row.date_start },
            { name: 'End', selector: (row) => row.date_end },
            {
              name: 'Status',
              selector: (row) => <FinanceStatusBadge status={row.status} />,
            },
            {
              name: 'Actions',
              selector: (row) => (
                <FinanceListActions
                  actions={[
                    {
                      key: 'soft-close',
                      label: 'Soft Close',
                      tone: 'warning',
                      onClick: () =>
                        handleAction(
                          softClosePeriod(row.id),
                          'Period soft closed',
                        ),
                    },
                    {
                      key: 'hard-close',
                      label: 'Hard Close',
                      tone: 'danger',
                      onClick: () =>
                        handleAction(
                          hardClosePeriod(row.id),
                          'Period hard closed',
                        ),
                    },
                    {
                      key: 'reopen',
                      label: 'Reopen',
                      tone: 'success',
                      onClick: () =>
                        handleAction(reopenPeriod(row.id), 'Period reopened'),
                    },
                  ]}
                />
              ),
            },
          ]}
          data={periods.data || []}
          searchable
          pagination
        />
      </FinancePageShell>
      <FinanceEntityDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Create Period"
        width="32%"
      >
        <DcInput
          value={form.name}
          placeholder="Period name"
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <DcInput
          type="date"
          value={form.date_start}
          onChange={(e) => setForm({ ...form, date_start: e.target.value })}
        />
        <DcInput
          type="date"
          value={form.date_end}
          onChange={(e) => setForm({ ...form, date_end: e.target.value })}
        />
        <DcButton onClick={handleCreate}>Save Period</DcButton>
      </FinanceEntityDrawer>
    </>
  )
}

export default Periods
